import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Calendar, Clock, User, ArrowRight, FileText, 
  Eye, Share2, ChevronDown, ChevronUp, BookOpen, Zap,
  CheckCircle, Target, Shield
} from 'lucide-react';
import { analytics } from '../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images générées par catégorie/slug
const ARTICLE_IMAGES = {
  'facturation-electronique-2026-guide-complet': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ca17ff8912835584b082f13bc0ab2607f565460cc002c370e4accc7c67ffa2e7.png',
  'facturation-electronique-freelance-guide': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/56d0e3e01b80a87abda8b3ad32c5cdff1fa09ecc6d2c4175469376dac195660a.png',
  'facturation-electronique-auto-entrepreneur': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/56d0e3e01b80a87abda8b3ad32c5cdff1fa09ecc6d2c4175469376dac195660a.png',
  'facturation-electronique-artisan-guide': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/650a39987b5f657c16999fdb19f2d02103d5cf8d7600914485c7071473bd7206.png',
  'facturation-electronique-pme-guide': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/2f1eeaa00b28b35fe3e78e7949ba728fadfde2cfc5f01a3091f99d6e5908fab3.png',
  'eviter-impayes-logiciel-facturation': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ec8c975d55c80fda523fad61a6b783f46153095c79127726a0ab22f9992f12cd.png',
  'intelligence-artificielle-automatiser-facturation': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/42726dfcc31c2eb59ee262545f2089600a96a315995485f1c91cf067d2606f16.png'
};

// Image par défaut par catégorie
const CATEGORY_IMAGES = {
  'Facturation électronique 2026': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ca17ff8912835584b082f13bc0ab2607f565460cc002c370e4accc7c67ffa2e7.png',
  'IA et automatisation comptable': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/42726dfcc31c2eb59ee262545f2089600a96a315995485f1c91cf067d2606f16.png',
  'Gestion des impayés': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ec8c975d55c80fda523fad61a6b783f46153095c79127726a0ab22f9992f12cd.png',
  'Guides par métier': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/56d0e3e01b80a87abda8b3ad32c5cdff1fa09ecc6d2c4175469376dac195660a.png',
  'default': 'https://static.prod-images.emergentagent.com/jobs/e195386f-f80b-4538-af09-25780105987d/images/ca17ff8912835584b082f13bc0ab2607f565460cc002c370e4accc7c67ffa2e7.png'
};

// Couleurs par catégorie
const categoryColors = {
  'Facturation électronique 2026': 'blue',
  'IA et automatisation comptable': 'violet',
  'Gestion des impayés': 'amber',
  'Guides par métier': 'emerald',
  'Réglementation': 'blue',
  'Technique': 'violet',
  'Guide': 'emerald',
  'Comptabilité': 'amber',
  'Tutoriel': 'cyan',
  'Partenaires': 'rose'
};

const BlogArticlePageSEO = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/blog/articles/${slug}`);
      if (!res.ok) throw new Error('Article non trouvé');
      const data = await res.json();
      setArticle(data);
      
      // Track blog view
      analytics.trackBlogView(slug, data.title);
      
      // Fetch related articles
      fetchRelatedArticles(data.category);
    } catch (error) {
      console.error('Erreur:', error);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedArticles = async (category) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/articles?category=${encodeURIComponent(category)}&limit=4`);
      const data = await res.json();
      // Filter out current article
      setRelatedArticles((data.articles || []).filter(a => a.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Erreur articles liés:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-3 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Article non trouvé</h1>
          <Link 
            to="/blog"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  const color = categoryColors[article.category] || 'blue';

  // Generate FAQ Schema
  const faqSchema = article.faq && article.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Generate Article Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt,
    "author": {
      "@type": "Organization",
      "name": "SmartFacture AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SmartFacture AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://smartfacture-ai.com/logo.png"
      }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://smartfacture-ai.com/blog/${article.slug}`
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{article.meta_title || `${article.title} | SmartFacture AI`}</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        <meta name="keywords" content={article.tags?.join(', ')} />
        <link rel="canonical" href={`https://smartfacture-ai.com/blog/${article.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://smartfacture-ai.com/blog/${article.slug}`} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {/* Header */}
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900 dark:text-white">SmartFacture AI</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link 
                to="/blog"
                className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Blog
              </Link>
              <Link 
                to="/register"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-medium rounded-md shadow-lg shadow-blue-500/25 transition-all"
              >
                Essai gratuit
              </Link>
            </div>
          </div>
        </header>

        {/* Article Header */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="mb-6">
                  <Link 
                    to="/blog"
                    className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Tous les articles
                  </Link>
                </div>
                
                <Badge className={`bg-${color}-500/20 text-${color}-300 border-${color}-500/30 mb-4`}>
                  {article.category}
                </Badge>
                
                {/* H1 - Main Title */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  {article.title}
                </h1>
                
                <p className="text-xl text-slate-300 mb-8 max-w-3xl">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(article.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {article.reading_time || 5} min de lecture
                  </span>
                  {article.views > 0 && (
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {article.views} lectures
                    </span>
                  )}
                  <button 
                    onClick={shareArticle}
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                  >
                <Share2 className="h-4 w-4" />
                Partager
              </button>
            </div>
              </div>
              
              {/* Article Image */}
              <div className="hidden lg:block">
                <img 
                  src={ARTICLE_IMAGES[article.slug] || CATEGORY_IMAGES[article.category] || CATEGORY_IMAGES['default']}
                  alt={article.title}
                  className="rounded-2xl shadow-2xl"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Main Content */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6 sm:p-10 mb-12">
              <div className="prose prose-lg dark:prose-invert max-w-none
                prose-headings:font-bold prose-headings:text-slate-900 dark:prose-headings:text-white
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:border-slate-200 dark:prose-h2:border-slate-700 prose-h2:pb-2
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed
                prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-slate-900 dark:prose-strong:text-white
                prose-ul:list-disc prose-ul:pl-6
                prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-slate-600 dark:prose-li:text-slate-300
                prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 dark:prose-blockquote:bg-blue-900/20 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:italic
                prose-table:border-collapse prose-table:w-full
                prose-th:bg-slate-100 dark:prose-th:bg-slate-800 prose-th:p-3 prose-th:text-left prose-th:font-semibold
                prose-td:p-3 prose-td:border-b prose-td:border-slate-200 dark:prose-td:border-slate-700
                prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
                prose-hr:border-slate-200 dark:prose-hr:border-slate-700
              ">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {article.content}
                </ReactMarkdown>
              </div>
            </div>

            {/* Inline CTA - Mid Article */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-8 mb-12 text-white text-center">
              <Zap className="h-12 w-12 mx-auto mb-4 opacity-90" />
              <h2 className="text-2xl font-bold mb-3">Essayez SmartFacture AI gratuitement</h2>
              <p className="text-blue-100 mb-6 max-w-xl mx-auto">
                Simplifiez votre facturation, automatisez vos relances et préparez-vous à la réforme 2026 en quelques minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-colors"
                >
                  Essai gratuit 7 jours
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
                <Link 
                  to="/demo"
                  className="inline-flex items-center justify-center px-6 py-3 border border-white text-white hover:bg-white/10 font-semibold rounded-lg transition-colors"
                >
                  Voir la démo
                </Link>
              </div>
              <p className="text-sm text-blue-200 mt-4">Sans carte bancaire - Annulation à tout moment</p>
            </div>

            {/* FAQ Section */}
            {article.faq && article.faq.length > 0 && (
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6 sm:p-10 mb-12">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-blue-500" />
                  Questions fréquentes
                </h2>
                <div className="space-y-4">
                  {article.faq.map((item, index) => (
                    <div 
                      key={index}
                      className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <span className="font-semibold text-slate-900 dark:text-white pr-4">
                          {item.question}
                        </span>
                        {openFaq === index ? (
                          <ChevronUp className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-6 py-4 bg-white dark:bg-slate-900">
                          <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <Target className="h-6 w-6 text-blue-500" />
                  Articles recommandés
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <Link 
                      key={related.slug} 
                      to={`/blog/${related.slug}`}
                      className="group"
                    >
                      <Card className="h-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-xl hover:border-blue-500/30 transition-all">
                        <CardContent className="p-5">
                          <Badge className={`mb-3 bg-${categoryColors[related.category] || 'blue'}-100 text-${categoryColors[related.category] || 'blue'}-700 dark:bg-${categoryColors[related.category] || 'blue'}-900/30 dark:text-${categoryColors[related.category] || 'blue'}-300 text-xs`}>
                            {related.category}
                          </Badge>
                          <h3 className="font-bold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {related.title}
                          </h3>
                          <p className="text-sm text-slate-500 line-clamp-2">
                            {related.excerpt}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-12">
                {article.tags.map((tag, index) => (
                  <Badge 
                    key={index}
                    variant="outline" 
                    className="text-slate-500 border-slate-300 dark:border-slate-600"
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Final CTA */}
            <div className="bg-slate-900 dark:bg-slate-800 rounded-2xl p-8 text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 text-cyan-400" />
              <h2 className="text-2xl font-bold text-white mb-3">
                Prêt pour la facturation électronique 2026 ?
              </h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto">
                Rejoignez les entreprises qui ont déjà fait le choix de SmartFacture AI pour leur mise en conformité.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-lg transition-all"
                >
                  Commencer gratuitement
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
                <Link 
                  to="/simulateur"
                  className="inline-flex items-center justify-center px-6 py-3 border border-slate-600 text-white hover:bg-slate-800 font-semibold rounded-lg transition-colors"
                >
                  Tester ma conformité
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-6 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  7 jours gratuits
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Sans carte bancaire
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  Support inclus
                </span>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8 border-t border-slate-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogArticlePageSEO;
